import { calculatePayment } from "./lib/calculatePayment";
import { calculateCreditByPayment } from "./lib/calculateCreditByPayment";
import { calculateAnnuityPayment } from "./lib/calculateAnnuityPayment";
import { createBankResult } from "./lib/createBankResult";
import { getPaymentByRate } from "./lib/calculatePaymentByRate";
import { calculatePromosCount } from "./lib/calculatePromosCount";
import { optionTypes } from "@/calculator/shared/Classes/BankClass";
import { calculateLivingExpenses } from "./lib/calculateLivingExpenses";
import { calculateCardBurden } from "./lib/calculateCardBurden";
export const getters = {
    rawBanks: (state, getters) => {
        return state.result?.banks || [];
    },
    getRate: (state, getters, rootState, rootGetters) => (id, rate = "minRate") => {
        const bank = rootState["calculator"].banks[id];
        const _dep = rootState["calculator"].financialBurden.rateReductionPayment;
        if (!bank) {
            return "0";
        }
        let calculatedRate = bank[rate];
        let plusAmount = 0;
        for (const type of optionTypes.plus) {
            const value = bank[type].applied ? bank[type].value : 0;
            if (bank.insuranceIsSum) {
                plusAmount += value;
            }
            else if (plusAmount < value) {
                plusAmount = value;
            }
        }
        calculatedRate += plusAmount;
        [...optionTypes.minus].forEach((type) => {
            bank[type].applied
                ? (calculatedRate -= bank[type].value)
                : (calculatedRate += 0);
        });
        if (bank.currentPaidRateReduction && bank.currentPaidRateReduction.active) {
            calculatedRate -= bank.currentPaidRateReduction.discount;
        }
        return Number(calculatedRate)
            .toFixed(2)
            .replace(/0*$/, "")
            .replace(/\.$/, "");
    },
    inputsValues: (state, getters, rootState, rootGetters) => {
        const inputs = rootGetters["calculator/calculatorInputs"];
        const value = {};
        inputs.forEach((input) => {
            value[input.id] = input.value;
        });
        return value;
    },
    calculationType: (state, getters) => {
        const inputsValues = getters.inputsValues;
        if (inputsValues[382] === 2755) {
            return "byIslamic";
        }
        if (inputsValues[-1] === 2515) {
            return "byCard";
        }
        if (inputsValues[2297] === 3015 || inputsValues[2300] === 3015) {
            return "byIncome";
        }
        return "bySum";
    },
    income: (state, getters) => {
        const inputsValues = getters.inputsValues;
        return inputsValues[2298] || inputsValues[2301];
    },
    isConsumerCredit(state, getters) {
        const inputsValues = getters.inputsValues;
        return (inputsValues[-1] === 2514 ||
            inputsValues[-1] === 2515 ||
            inputsValues[-1] === 2516 ||
            inputsValues[-1] === 2801 ||
            inputsValues[-1] === 2896 ||
            inputsValues[-2] === 3);
    },
    banks: (state, getters, rootState, rootGetters) => {
        const banks = getters.rawBanks;
        if (!banks.length)
            return [];
        const { income, inputsValues: inputs, calculationType: calcType, isConsumerCredit, } = getters;
        const { financialBurden, banksLogos: logos } = rootState["calculator"];
        const bankStates = rootState["calculator"].banks;
        const creditPayments = financialBurden?.monthlyLoanPayments || 0;
        const creditSum = rootGetters["calculator/getFullCreditSum"];
        const term = inputs[153] || inputs[17] || 0;
        const firstPayment = inputs[2299] || inputs[2302] || inputs[1965] || 0;
        return banks.map((bank) => {
            const bankState = bankStates[bank.id];
            if (!bankState) {
                return null;
            }
            const logoData = logos[bank.id_for_marketplace];
            const isMaxRate = bank.minRate !== bank.maxRate;
            const rateDecimal = getters.getRate(bank.id, isMaxRate ? "maxRate" : "minRate") / 100;
            const monthRate = rateDecimal / 12;
            const commonParams = {
                logo: logoData.logo,
                license: logoData.license,
                promosCount: calculatePromosCount(bankState),
                activePromosCount: bankState.promoActive,
                isMaxRate,
                rate: rateDecimal * 100,
            };
            switch (calcType) {
                case "byIslamic": {
                    const payment = inputs[-1] === 2515
                        ? Number(rateDecimal) * creditSum
                        : getPaymentByRate(creditSum, term, monthRate);
                    const cardPayments = calculateCardBurden(financialBurden, bank, isConsumerCredit);
                    const { lifeCost, childOutcome } = calculateLivingExpenses(financialBurden, bank, isConsumerCredit);
                    const paymentRate = isConsumerCredit
                        ? bank.paramValues[1362]
                        : bank.paramValues[193];
                    const totalObligations = payment + creditPayments + cardPayments;
                    const incomeByDTI = Math.ceil(totalObligations / (paymentRate / 100));
                    const incomeByLifeCost = Math.ceil(lifeCost + childOutcome + totalObligations);
                    return createBankResult(bank, {
                        ...commonParams,
                        income: Math.max(incomeByDTI, incomeByLifeCost),
                        payment,
                    }, "byIslamic");
                }
                case "byCard": {
                    const payment = bank.creditCardLimitPercentMin;
                    return createBankResult(bank, {
                        ...commonParams,
                        payment,
                    }, "byCard");
                }
                case "byIncome": {
                    const paymentPercent = bank.paymentPercent / 100 || 0.5;
                    const limitPercent = calculateCardBurden(financialBurden, bank, isConsumerCredit);
                    const { lifeCost, livingWage } = calculateLivingExpenses(financialBurden, bank, isConsumerCredit);
                    const calculatedPayment = calculatePayment(income, paymentPercent, creditPayments, limitPercent, lifeCost, livingWage);
                    const initialCredit = calculateCreditByPayment(calculatedPayment, rateDecimal, term);
                    const maxCredit = bank.maxSumCreditWithRestrictions;
                    let finalParams;
                    if (initialCredit > maxCredit) {
                        const annuityPayment = calculateAnnuityPayment(maxCredit, rateDecimal, term);
                        finalParams = {
                            amount: maxCredit + firstPayment,
                            credit: maxCredit,
                            income,
                            overpayment: annuityPayment * term - maxCredit,
                            payment: calculatedPayment,
                        };
                    }
                    else {
                        finalParams = {
                            amount: initialCredit + firstPayment,
                            credit: initialCredit,
                            income,
                            overpayment: calculatedPayment * term - initialCredit,
                            payment: calculatedPayment,
                        };
                    }
                    return createBankResult(bank, {
                        ...commonParams,
                        ...finalParams,
                    }, "byIncome");
                }
                case "bySum": {
                    const cardPayments = calculateCardBurden(financialBurden, bank, isConsumerCredit);
                    const { lifeCost, childOutcome } = calculateLivingExpenses(financialBurden, bank, isConsumerCredit);
                    const paymentRate = isConsumerCredit
                        ? bank.paramValues[1362]
                        : bank.paramValues[193];
                    const payment = inputs[-1] === 2515
                        ? Number(rateDecimal) * creditSum
                        : getPaymentByRate(creditSum, term, monthRate);
                    const totalObligations = payment + creditPayments + cardPayments;
                    const incomeByDTI = Math.ceil(totalObligations / (paymentRate / 100));
                    const incomeByLifeCost = Math.ceil(lifeCost + childOutcome + totalObligations);
                    return createBankResult(bank, {
                        ...commonParams,
                        income: Math.max(incomeByDTI, incomeByLifeCost),
                        overpayment: payment * term - creditSum,
                        payment,
                    }, "bySum");
                }
                default:
                    throw new Error(`Неизвестный тип: ${calcType}`);
            }
        });
    },
    sortedBanks: (state, getters) => {
        const banks = getters.banks;
        const orderedIds = state.orderedBankIds;
        if (!orderedIds.length)
            return banks;
        const bankMap = new Map(banks.map((b) => [b.id, b]));
        const sortedResult = orderedIds
            .map((id) => bankMap.get(id))
            .filter((b) => b !== undefined);
        if (sortedResult.length !== banks.length) {
            const processedIds = new Set(sortedResult.map((b) => b.id));
            const newBanks = banks.filter((b) => !processedIds.has(b.id));
            return [...sortedResult, ...newBanks];
        }
        return sortedResult;
    },
    comparedBanks: (state, getters) => {
        const sortedBanks = getters.sortedBanks;
        const comparedBanks = sortedBanks.filter((bank) => state.comparedBanks.includes(bank.id));
        return comparedBanks;
    },
    hiddenBanks: (state, getters) => {
        const sortedBanks = getters.sortedBanks;
        const hiddenBanks = sortedBanks.filter((bank) => state.hiddenBanks.includes(bank.id));
        return hiddenBanks;
    },
    visibleBanks: (state, getters) => {
        const sortedBanks = getters.sortedBanks;
        return sortedBanks.filter((bank) => !state.hiddenBanks.includes(bank.id));
    },
    displayedBanks: (state, getters) => {
        switch (state.currentView) {
            case "compared":
                return getters.comparedBanks;
            case "hidden":
                return getters.hiddenBanks;
            case "all":
            default:
                return getters.visibleBanks;
        }
    },
    banksCount: (state, getters) => {
        return getters.banks.length;
    },
    comparedBanksCount: (state) => {
        return state.comparedBanks.length;
    },
    hiddenBanksCount: (state) => {
        return state.hiddenBanks.length;
    },
    isCompared: (state) => (bankId) => {
        return state.comparedBanks.includes(bankId);
    },
    isHidden: (state) => (bankId) => {
        return state.hiddenBanks.includes(bankId);
    },
    sortOptions: (state, getters, rootState, rootGetters) => {
        const isIslamicCredit = rootGetters["calculator/getIsIslamicCredit"];
        const isCreditCard = rootGetters["calculator/getIsCreditCardCredit"];
        const isIncomeCredit = rootGetters["calculator/getIsIncomeCredit"];
        const banks = getters.banks;
        const firstBank = banks[0];
        const options = [
            // Ставка
            { label: "Ставка", field: "rate", direction: "asc" },
            { label: "Ставка", field: "rate", direction: "desc" },
            // Платёж
            { label: "Платёж", field: "payment", direction: "asc" },
            { label: "Платёж", field: "payment", direction: "desc" },
            // Доход
            { label: "Доход", field: "income", direction: "asc" },
            { label: "Доход", field: "income", direction: "desc" },
            // Переплата
            { label: "Переплата", field: "overpayment", direction: "asc" },
            { label: "Переплата", field: "overpayment", direction: "desc" },
            // Кредит
            { label: "Кредит", field: "credit", direction: "asc" },
            { label: "Кредит", field: "credit", direction: "desc" },
        ];
        const filtered = options.filter((option) => {
            // Если нет банков, показываем все опции
            if (!firstBank)
                return true;
            // Проверяем наличие поля в объекте банка
            const fieldExists = option.field in firstBank;
            // Дополнительная проверка для исламского кредита
            if (option.field === "rate" && isIslamicCredit)
                return false;
            return fieldExists;
        });
        return filtered.map((options) => ({
            ...options,
            id: `${options.field}-${options.direction}`,
        }));
    },
    minOption: (state, getters) => (field) => {
        const banks = getters.banks;
        if (!banks || banks.length === 0) {
            return 0;
        }
        const values = banks
            .filter((b) => b && b[field] !== undefined && b[field] !== null)
            .map((bank) => bank[field]);
        if (values.length === 0)
            return 0;
        return Math.min(...values);
    },
    minRate: (state, getters) => {
        return getters.minOption("rate");
    },
    minPayment: (state, getters) => {
        if (getters.banks[0]?.type === "byCard") {
            return 0;
        }
        return getters.minOption("payment");
    },
};
